import {useCallback, useEffect, useRef} from 'react';
import {useLatest} from 'ahooks';
import {isConnectedAccountsSelectionModalOpen, openConnectedAccountsSelectionModal} from '@Modals/connected-accounts-selection-modal/connected-accounts-selection-modal.library';
import {getAccountIdsForPlatformFamily, getNewlyConnectedAccountIds} from '@Libraries/social-media-account.library';
import {getPostableSocialMediaAccountsForUser} from '@Components/social-media/social-media-thunk';
import {useSocialMediaWizardAccounts} from '@Components/social-media/social-media.hooks';
import {MAX_NEW_ACCOUNTS_WITHOUT_SELECTION_MODAL} from './social-accounts-list.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {SocialMediaAccountId} from '@Components/social-media/post.vo';
import {addAccountIdForPublishing, updateTriggerNext} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {getComparableSocialPlatform} from '@Libraries/social-media-platform.library';
import {useAppDispatch} from '@/hooks';
import type {AccountNeedsResizeMap, SocialPlatform} from '@Components/social-media/social-media.types';

const MAX_AUTO_SELECT_NEW_CONNECTED_ACCOUNTS = 1;

interface ReloadAccountsAndOfferSelectionOpts {
  advanceToReviewOnFirstConnection?: boolean;
}

interface UseAccountPreselectionOpts {
  accounts: SocialAccount[];
  availableAccounts: SocialAccount[];
  accountIdsToPublishTo: SocialMediaAccountId[];
  accountNeedsResizeMapForDesigns: AccountNeedsResizeMap;
  emphasizedPlatform: SocialPlatform | undefined;
  isListLoaded: boolean;
  areAccountsLoading: boolean;
  setIsListLoaded: (loaded: boolean) => void;
  isSelectionControlled: boolean;
  isAccountPublishable: (accountId: number) => boolean;
  onAccountSelected: (idAccount: number) => void;
  replaceSelection: (accountIds: number[]) => void;
}

export function useAccountPreselection({
  accounts,
  availableAccounts,
  accountIdsToPublishTo,
  accountNeedsResizeMapForDesigns,
  emphasizedPlatform,
  isListLoaded,
  areAccountsLoading,
  setIsListLoaded,
  isSelectionControlled,
  isAccountPublishable,
  onAccountSelected,
  replaceSelection,
}: UseAccountPreselectionOpts): void {
  const prevAccountsRef = useRef<SocialAccount[] | undefined>(undefined);

  const hasResizeMapBeenInitialized = (): boolean => {
    return Object.keys(accountNeedsResizeMapForDesigns).length !== 0;
  };

  const shouldPreselectSpecificPlatform = (): boolean => {
    return !!emphasizedPlatform;
  };

  const canPreferredAccountsBeSet = (): boolean => {
    return !isListLoaded && accounts.length > 0 && accountIdsToPublishTo.length === 0 && hasResizeMapBeenInitialized();
  };

  const getPublishableAccountIdsMatching = (matchesPlatform: (account: SocialAccount) => boolean): SocialMediaAccountId[] => {
    const accountIdsToSelect: SocialMediaAccountId[] = [];

    availableAccounts.forEach((account) => {
      if (matchesPlatform(account) && isAccountPublishable(account.id)) {
        accountIdsToSelect.push(account.id);
      }
    });

    return accountIdsToSelect;
  };

  const getAccountIdsInPlatformFamily = (platformFamily: SocialPlatform): SocialMediaAccountId[] => {
    return getPublishableAccountIdsMatching((account) => {
      return getComparableSocialPlatform(account.type) === getComparableSocialPlatform(platformFamily);
    });
  };

  const getAccountIdsForEmphasizedPlatform = (): SocialMediaAccountId[] => {
    const accountIdsMatchingStoredType = getPublishableAccountIdsMatching((account) => {
      return account.type === emphasizedPlatform;
    });

    if (accountIdsMatchingStoredType.length > 0 || emphasizedPlatform === undefined) {
      return accountIdsMatchingStoredType;
    }

    return getAccountIdsInPlatformFamily(emphasizedPlatform);
  };

  const getPreferredAccountIds = (): SocialMediaAccountId[] => {
    const preferredAccountIds: SocialMediaAccountId[] = [];

    availableAccounts.forEach((account) => {
      if (account.isPreferred && isAccountPublishable(account.id)) {
        preferredAccountIds.push(account.id);
      }
    });

    return preferredAccountIds;
  };

  const getValidatedAccountIds = (): SocialMediaAccountId[] => {
    const validatedAccountIds: SocialMediaAccountId[] = [];

    accountIdsToPublishTo.forEach((accountId) => {
      if (isAccountPublishable(accountId)) {
        validatedAccountIds.push(accountId);
      }
    });

    return validatedAccountIds;
  };

  const selectNewConnectedAccount = (previousAccounts: SocialAccount[], currentAccounts: SocialAccount[]): void => {
    if (currentAccounts.length - previousAccounts.length > MAX_AUTO_SELECT_NEW_CONNECTED_ACCOUNTS) {
      return;
    }

    const previousIds = previousAccounts.map((account) => {
      return account.id;
    });
    const currentIds = currentAccounts.map((account) => {
      return account.id;
    });

    const newAccountId = currentIds.filter((id) => {
      return !previousIds.includes(id);
    });

    const publishableAccounts = newAccountId.filter((accountId) => {
      return isAccountPublishable(accountId);
    });

    publishableAccounts.forEach((accountId) => {
      onAccountSelected(accountId);
    });
  };

  useEffect(() => {
    if (areAccountsLoading || isSelectionControlled) {
      return;
    }

    if (shouldPreselectSpecificPlatform()) {
      replaceSelection(getAccountIdsForEmphasizedPlatform());
    } else if (canPreferredAccountsBeSet()) {
      replaceSelection(getPreferredAccountIds());
      setIsListLoaded(true);
    } else {
      replaceSelection(getValidatedAccountIds());
    }
  }, [accounts, accountNeedsResizeMapForDesigns, areAccountsLoading]);

  useEffect(() => {
    if (prevAccountsRef.current) {
      selectNewConnectedAccount(prevAccountsRef.current, accounts);
    }
    prevAccountsRef.current = accounts;
  }, [accounts]);
}

export function useReloadAccountsAndOfferSelection(): (connectedPlatform: SocialPlatform | undefined, options?: ReloadAccountsAndOfferSelectionOpts) => Promise<void> {
  const dispatch = useAppDispatch();
  const accounts = useSocialMediaWizardAccounts();
  const accountsRef = useLatest(accounts);

  return useCallback(
    async (connectedPlatform: SocialPlatform | undefined, options?: ReloadAccountsAndOfferSelectionOpts): Promise<void> => {
      if (isConnectedAccountsSelectionModalOpen()) {
        return;
      }

      const accountsBeforeConnection = accountsRef.current;
      const refetchResult = await dispatch(getPostableSocialMediaAccountsForUser());

      if (!getPostableSocialMediaAccountsForUser.fulfilled.match(refetchResult)) {
        return;
      }

      const newlyConnectedAccountIds = getNewlyConnectedAccountIds(accountsBeforeConnection, refetchResult.payload.jsonAccounts);
      const accountIdsCoveredByConnection =
        connectedPlatform !== undefined ? getAccountIdsForPlatformFamily(refetchResult.payload.jsonAccounts, connectedPlatform) : newlyConnectedAccountIds;

      const shouldAdvanceToReview = (options?.advanceToReviewOnFirstConnection ?? false) && accountsBeforeConnection.length === 0 && newlyConnectedAccountIds.length > 0;

      if (accountIdsCoveredByConnection.length > MAX_NEW_ACCOUNTS_WITHOUT_SELECTION_MODAL) {
        openConnectedAccountsSelectionModal({accountIds: accountIdsCoveredByConnection, newlyConnectedAccountIds, advanceToReviewOnConfirm: shouldAdvanceToReview});
        return;
      }

      if (shouldAdvanceToReview) {
        newlyConnectedAccountIds.forEach((accountId) => {
          dispatch(addAccountIdForPublishing(accountId));
        });
        dispatch(updateTriggerNext());
      }
    },
    [dispatch]
  );
}
